/**
 * Notification scopes. The backend writes Channel Partner events into the
 * same Firestore feed as ordinary account events and tells them apart with
 * `scope` (FinMin_Backend app/notifications/channels/firestore.py):
 *
 *   account          → the ordinary dashboard bell (older items lack `scope`)
 *   channel_partner  → the CP Hub bell only
 *
 * A CP event also appends one account-scoped summary item
 * (`channel_partner_dashboard_summary`) that points the user at the Hub.
 */
export type NotificationScope = 'account' | 'channel_partner';

export const CP_DASHBOARD_SUMMARY_EVENT = 'channel_partner_dashboard_summary';

/** Path prefix of the CP Hub, where CP-scoped items belong. */
export const CHANNEL_PARTNER_HUB_PATH = '/channel-partner/hub';

/** Missing or unknown scope means an ordinary account item. */
export function normalizeNotificationScope(raw: unknown): NotificationScope {
  return raw === 'channel_partner' ? 'channel_partner' : 'account';
}

export function isChannelPartnerHubPath(pathname: string): boolean {
  return pathname === CHANNEL_PARTNER_HUB_PATH || pathname.startsWith(`${CHANNEL_PARTNER_HUB_PATH}/`);
}

/**
 * Toast only what belongs to the surface the user is on: CP items inside the
 * Hub, account items elsewhere — and never the Hub summary while already in
 * the Hub (it would duplicate the CP item it summarises).
 */
export function shouldToastNotification(
  n: { scope: NotificationScope; event: string },
  inHub: boolean,
): boolean {
  if (n.scope === 'channel_partner') return inHub;
  if (n.event === CP_DASHBOARD_SUMMARY_EVENT) return !inHub;
  return true;
}
